import { Grid } from '@mui/joy';
import { capitalize } from 'lodash';
import { useTranslation } from 'react-i18next';

import { InfoCard, SlidingPanelContent } from 'core/components';
import { Loader } from 'core/elements';
import { getLabel } from 'core/utils';
import { CorporatePartNotSetUp } from 'corporate/components/CorporatePartNotSetUp';
import { CorporatePartHelper } from 'corporate/helpers';
import { ICorporatePart } from 'corporate/types';

import { CorporatePartUpsertButton } from './CorporatePartUpsertButton';

interface IProps {
  corporatePart?: Partial<ICorporatePart> | null;
  partNumber: string;
  manufacturerId: number;
  fullWidth?: boolean;
  isLoading?: boolean;
  scrollableContent?: boolean;
}

export const CorporatePartSetUpList = ({
  corporatePart,
  partNumber,
  manufacturerId,
  fullWidth = true,
  isLoading = false,
  scrollableContent = false,
}: IProps) => {
  const { t } = useTranslation();

  const isSetUp = CorporatePartHelper.isSetUp(corporatePart);

  const firstInfoCardItemsMap = !isSetUp
    ? []
    : [
        { label: t('labelType'), value: capitalize(corporatePart.packageType) },
        { label: t('labelSalesMinimumOrderQuantity'), value: corporatePart.minimumQuantityWithUom },
        { label: t('labelSalesOrderIncrements'), value: corporatePart.orderIncrementWithUom },
        { label: t('labelPackageQuantity'), value: corporatePart.pkgUnitsWithUom },
        { label: t('labelBomPriceDecimals'), value: corporatePart.bomPriceDecimals },
        { label: t('labelQuotePriceDecimals'), value: corporatePart.quotePriceDecimals },
        {
          label: t('labelVendorMinimumOrderQuantity'),
          value: corporatePart.manufacturerMinimumQuantityWithUom,
        },
        {
          label: t('labelVendorOrderIncrements'),
          value: corporatePart.manufacturerOrderIncrementWithUom,
        },
        { label: t('labelClass'), value: corporatePart.className },
        { label: t('labelTaaStatus'), value: getLabel(corporatePart.taaStatus) },
        { label: t('labelCompanyPartStatus'), value: getLabel(corporatePart.companyPartStatus) },
        { label: t('labelCompanyPartNumber'), value: corporatePart.vendorNumber },
        { label: t('labelCompanyManufacturerName'), value: corporatePart.companyManufacturerName },
        { label: t('labelCompanyManufacturerId'), value: corporatePart.companyManufacturerId },
        { label: t('labelManufacturerPartStatus'), value: getLabel(corporatePart.mfgPartStatus) },
        { label: t('labelReplacementPart'), value: corporatePart.replacementPart },
      ];

  const secondInfoCardItemsMap = !isSetUp
    ? []
    : [
        { label: t('labelPartBaseCode'), value: corporatePart.partBaseCode },
        { label: t('labelPricingGroupCode1'), value: corporatePart.pricingGroup1 },
        {
          label: t('labelPricingGroupDescription1'),
          value: corporatePart.pricingGroupDescription1,
        },
        { label: t('labelPricingGroupCode2'), value: corporatePart.pricingGroup2 },
        {
          label: t('labelPricingGroupDescription2'),
          value: corporatePart.pricingGroupDescription2,
        },
        { label: t('labelPricingGroupCode3'), value: corporatePart.pricingGroup3 },
        {
          label: t('labelPricingGroupDescription3'),
          value: corporatePart.pricingGroupDescription3,
        },
        { label: t('labelPricingGroupCode4'), value: corporatePart.pricingGroup4 },
        {
          label: t('labelPricingGroupDescription4'),
          value: corporatePart.pricingGroupDescription4,
        },
        { label: t('labelPricingGroupCode5'), value: corporatePart.pricingGroup5 },
        {
          label: t('labelPricingGroupDescription5'),
          value: corporatePart.pricingGroupDescription5,
        },
      ];

  return (
    <SlidingPanelContent
      title={t('titleSetUp')}
      fullWidth={fullWidth}
      extra={
        !isLoading && (
          <CorporatePartUpsertButton
            corporatePart={corporatePart}
            partNumber={partNumber}
            manufacturerId={manufacturerId}
          />
        )
      }
      scrollableContent={scrollableContent}
    >
      {isLoading && <Loader />}
      {!isLoading && !isSetUp && <CorporatePartNotSetUp sx={{ marginTop: '0 !important' }} />}
      {!isLoading && isSetUp && (
        <Grid
          container
          spacing={3}
          sx={{ marginLeft: '-16px !important', marginRight: '-16px !important' }}
        >
          <Grid xs={12} md={6}>
            <InfoCard items={firstInfoCardItemsMap} />
          </Grid>
          <Grid xs={12} md={6}>
            <InfoCard items={secondInfoCardItemsMap} />
          </Grid>
        </Grid>
      )}
    </SlidingPanelContent>
  );
};
